import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { api } from '@marine/api-client/api';
import { BrandedAuthLayout } from '@/layouts/BrandedAuthLayout';
import { Input } from '@marine/ui/input';
import { Button } from '@marine/ui/button';
import { Alert } from '@marine/ui/alert';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    password_confirmation: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ['password_confirmation'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function PlatformResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = params.get('token');
  const email = params.get('email');

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', password_confirmation: '' },
  });

  const mutation = useMutation({
    mutationFn: async (data: ResetPasswordFormData) => {
      return api.post('/api/auth/reset-password', {
        email,
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
    },
    onSuccess: () => {
      navigate('/reset-password/changed');
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    mutation.mutate(data);
  };

  if (!token || !email) {
    return (
      <BrandedAuthLayout variant="admin">
        <div className="block w-full space-y-5 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Invalid reset link
          </h1>
          <p className="text-sm text-muted-foreground">
            This password reset link is missing required information. Please
            request a new one.
          </p>
          <Button asChild className="w-full">
            <Link to="/forgot-password">Request new link</Link>
          </Button>
        </div>
      </BrandedAuthLayout>
    );
  }

  return (
    <BrandedAuthLayout variant="admin">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="block w-full space-y-5"
        >
          <div className="text-center space-y-1 pb-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Set new password
            </h1>
            <p className="text-sm text-muted-foreground">
              Choose a strong password for{' '}
              <span className="font-medium text-foreground">{email}</span>
            </p>
          </div>

          {mutation.isError && (
            <Alert variant="destructive">
              <p className="text-sm">
                {getErrorMessage(
                  mutation.error,
                  'Could not reset password. Your link may have expired — request a new one.',
                )}
              </p>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <div className="relative">
                  <Input
                    placeholder="At least 8 characters"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    disabled={mutation.isPending}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm new password</FormLabel>
                <div className="relative">
                  <Input
                    placeholder="Re-enter your new password"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    disabled={mutation.isPending}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  >
                    {showConfirm ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Resetting...
              </span>
            ) : (
              'Reset password'
            )}
          </Button>
        </form>
      </Form>
    </BrandedAuthLayout>
  );
}
